import { functions } from "lodash";
import Config from "../constnats/config";
import { DescriptorProps } from "../constnats/descriptor";
import { MATH_OPERATION_SIGN, MATH_OPERATION_SIGNS } from "../constnats/math";
import { MetadataField } from "../constnats/reportConstants";
import EntityFactory, { CellEntity } from "../pages/admin/company/components/financialTables/table/cellEntity";
import { ReportType } from "../schemas/report.schemas";
import JsEventLogger from "../service/jsEventsLogger";
import Assert from "./asserts";
import CloneUtils from "./cloneUtils";
import { CalculationExpUtil } from "./descriptor/calculationExp";
import DescriptorType from "./descriptor/descriptorType";
import Utils from "./descriptor/descriptorUtils";
import ObjCheck from "./objCheck";
import ReportUtils from "./reportUtils";
import NumUtils  from './utils'

type ChangeType = {
    value: any;
    correction: any;
    calcsDisabled: any;
    manuallyCalculated: any;
    reportedAsLabel: any;
};

type FieldChangeType = {
    new: ChangeType;
    old: ChangeType;
};

type ReportChangesType = Record<string, FieldChangeType>;


export const UPDATA_STRATEGY = {
    REPLACE_CORE_VALUES: 'REPLACE_CORE_VALUES',
    PRESERVE_NON_EMPTY_CORE_VALUES: 'PRESERVE_NON_EMPTY_CORE_VALUES',
    APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES:
        'APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES',
} as const;

export function getChangeListener(report: ReportType, reportChanges: ReportChangesType) {
    return (descriptorChange: boolean, entity: CellEntity) => {
        const descriptorId = entity.descriptorId();
        const oldDataRaw = report.dynamicFields[descriptorId];
        const oldData = oldDataRaw === undefined ? '' : oldDataRaw;
        const fieldSpecificMetadata = report.metadata.fieldSpecific[descriptorId];
        const oldCorrectionData = fieldSpecificMetadata?.[MetadataField.CORRECTION] || '0';
        const oldCalcsDisabled = !!fieldSpecificMetadata?.[MetadataField.CALCS_DISABLED];
        const oldManuallyCalculated = !!fieldSpecificMetadata?.[MetadataField.MANUALLY_CALCED];
        const oldReportedAsLabel = fieldSpecificMetadata?.reportedAsLabel;

        if (descriptorChange === true) {
            const newMetadata = entity.getMetadata();
            const newData = entity.getData();
            const newCorrectionData = newMetadata[MetadataField.CORRECTION];
            const newCalcsDisabled = entity.getCalcsDisabled();
            const newManuallyCalculated = !!newMetadata[MetadataField.MANUALLY_CALCED];
            const newReportedAsLabel = newMetadata[MetadataField.REPORTED_AS_LABEL];
            if (!(newData === '-' &&
                ObjCheck.isNullUndefinedEmptyOrDash(oldData) &&
                newCorrectionData == oldCorrectionData &&
                normalizeBoolean(newCalcsDisabled) == normalizeBoolean(oldCalcsDisabled) &&
                normalizeBoolean(newManuallyCalculated) == normalizeBoolean(oldManuallyCalculated) &&
                normalizeLabel(newReportedAsLabel) == normalizeLabel(oldReportedAsLabel)
            )) {
                //TODO Find more elegant approach
                reportChanges[descriptorId] = {
                    new: {
                        value: newData,
                        correction: newCorrectionData,
                        calcsDisabled: newCalcsDisabled,
                        manuallyCalculated: newManuallyCalculated,
                        reportedAsLabel: oldReportedAsLabel,
                    },
                    old: {
                        value: oldData,
                        correction: oldCorrectionData,
                        calcsDisabled: oldCalcsDisabled,
                        manuallyCalculated: oldManuallyCalculated,
                        reportedAsLabel: newReportedAsLabel,
                    },
                };    
            }
        }
    };
}

function normalizeBoolean(b:any) {
    return b === true
}

function normalizeLabel(l:any) {
    return ObjCheck.isNullUndefinedOrEmpty(l) ? "" : l
}

export class ReportUpdateManager {
    correctionThreshold = 2; 
    report: ReportType;
    descriptors: any[];
    reportChanges: ReportChangesType;
    dryRunEntries: CellEntity[];
    descriptorsToUpdateStrategyMap: any;
    schemaId: number;
    companyId: number;

    constructor(report: ReportType, descriptors: any[], descriptorsToUpdateStrategyMap: any, schemaId: number, companyId: number) {
        this.report = report;
        this.descriptors = descriptors;
        this.reportChanges = {};
        this.descriptorsToUpdateStrategyMap = descriptorsToUpdateStrategyMap
        this.schemaId = schemaId;
        this.companyId = companyId;
        const changeListener = getChangeListener(this.report, this.reportChanges);

        this.dryRunEntries = new EntityFactory(changeListener).create(descriptors, CloneUtils.deepClone(this.report));

        for (const entity of this.dryRunEntries) {
            entity.refresh();
        }
    }

    /**
     * Sorts the dry run entries based on their dependencies.
     * This ensures that any entity that depends on another entity is processed after its dependencies.
     */
    private getDependencySortedEntries() {
        // Create a map for quick lookup by id
        const entitiesMap = new Map(this.dryRunEntries.map((entity) => [entity.descriptorId(), entity]));
        const visited = new Set<string>();
        const result: any[] = [];

        function visit(id: string) {
            // Already processed
            if (visited.has(id)) {
                return;
            }
            const entity = entitiesMap.get(id);

            let includedIds: string[] = [];
            const descriptor = entity.getDescriptor();
            if (Utils.hasCalculation(descriptor)) {
                includedIds = CalculationExpUtil.getIncludedIds(descriptor[DescriptorProps.CALCULATION]);
            }

            // Visit all dependencies first
            for (const depId of includedIds) {
                visit(depId);
            }

            visited.add(id);
            result.push(entity);
        }

        // Visit all objects
        for (const entity of entitiesMap.values()) {
            visit(entity.descriptorId());
        }

        return result;
    }

    remediate() { 
        for (const entity of this.getDependencySortedEntries()) {
            const descriptor = entity.getDescriptor();
            const descriptorId = descriptor[DescriptorProps.ID]

            const oldData = entity.getOriginalData().replace("%", "").trim();
            let newData = entity.getData().replace("%", "").trim();
            /**
             * We enter remediation only if old and new data are different and 
             * only if at least one of the data are not considered empty
             */
            if (newData !== oldData && 
                (!ObjCheck.isNullUndefinedEmptyDashOrSpace(newData) || 
                 !ObjCheck.isNullUndefinedEmptyDashOrSpace(oldData))) {
                /**
                 * descriptorsToUpdateStrategyMap holds dynamic descriptorIds (it missing year period format etc).
                 * Due to this we get an error if we try and access a non-dynamic descriptor id.
                 * Here we check if the descriptorsToUpdateStrategyMap has a value before checking to see if the strategy
                 * is included
                 */


                /**
                 * The old corrections cannot be used because the core number is changed so they become invalid.
                 */
                const metadata = entity.setMetadata({ ...entity.getMetadata(), [MetadataField.CORRECTION]: "0" });

                /**
                 * The new value is updated automatically because of the changed of the correction.
                 */
                newData = entity.getData().replace("%", "").trim();

                const strategies = this.descriptorsToUpdateStrategyMap[descriptorId] || []

                if (strategies.includes(UPDATA_STRATEGY.PRESERVE_NON_EMPTY_CORE_VALUES)) {
                        this.preserveCoreValues(metadata, oldData, newData, entity, descriptor);
                }

                if (strategies.includes(UPDATA_STRATEGY.APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES)) {
                    if (ObjCheck.isNullUndefinedEmptyOrDash(oldData)) {
                        entity.setMetadata({ ...metadata, [MetadataField.MANUALLY_CALCED]: true })
                    }
                }
            }
        }
    }

    private preserveCoreValues(metadata: any, oldData: any, newData: any, entity: CellEntity, descriptor: any) {
        const hasCalculation = Utils.hasCalculation(descriptor);
        let calculationFragments: any = [];
        let hasDivisionInCalculation = false;
        if (hasCalculation) {
            calculationFragments = CalculationExpUtil.strToFragments(descriptor[DescriptorProps.CALCULATION]);
            hasDivisionInCalculation = calculationFragments.includes(MATH_OPERATION_SIGN.DIVISION);
            this.logAnomalies(hasDivisionInCalculation, descriptor);
        }


        function preserveByCalcDisable() {
            entity.setMetadata({ ...metadata, [MetadataField.CALCS_DISABLED]: true, [MetadataField.CORRECTION]: "0" });
            if (DescriptorType.isPercent(descriptor)) {
                oldData = oldData + '%';
            }
            entity.setData(oldData);
        }

        function preserveByApplyingCorrection(correction: number) {
            /**
             * The core value is automatically returned to the old value because
             * of the automatic recalculations cause by setMetadata.
             */
            entity.setMetadata({
                ...metadata,
                [MetadataField.CORRECTION]: NumUtils.toReadMode(correction, Config.FLOATING_POINT_PRECISION), [MetadataField.CALCS_DISABLED]: false,
            });
        }

        if (hasCalculation && !metadata[MetadataField.MANUALLY_CALCED] && NumUtils.isNumber(oldData)) {

            if (NumUtils.isNumber(newData)) {
                /**
                 * Old num represents the corrected old data that needs to be preserved (the core data initially presented to the client).
                 */
                const oldNum = NumUtils.toNumber(oldData); 
                const newNum = NumUtils.toNumber(newData);

                const correction = (newNum - oldNum) * -1;
                const correctionThresholdExceeded = this.correctionThresholdExceeded(hasDivisionInCalculation, correction, calculationFragments, entity);
                if (correctionThresholdExceeded) {
                    preserveByCalcDisable()
                } else {
                    preserveByApplyingCorrection(correction)
                }
            } else {
                preserveByCalcDisable()
            }
        }

    }

    private correctionThresholdExceeded(hasDivisionInCalculation: boolean, correction: number, calculationFragments: any, entity: CellEntity) {
        correction = Math.abs(correction)
        let correctionThresholdExceeded = false;
        if (hasDivisionInCalculation) {
            correctionThresholdExceeded = correction > this.correctionThreshold;
        } else {
            const numberOfArguments = calculationFragments
                .flat()
                .filter((x) => !MATH_OPERATION_SIGNS.includes(x)).length;
            const maxAllowedDifference = (numberOfArguments / 2 + 1) * entity.getEffectiveInputScale();
            correctionThresholdExceeded = correction > maxAllowedDifference;
        }

        return correctionThresholdExceeded;
    }

    private logAnomalies(hasDivisionInCalculation: boolean, descriptor: any) {
        if (hasDivisionInCalculation && !DescriptorType.isPercent(descriptor)) {
            let jsEvent = {
                descriptorId: descriptor.id,
                reportId: this.report.id,
                schemaId: this.schemaId,
                companyId: this.companyId,
            };
            JsEventLogger.log(
                JSON.stringify(jsEvent),
                'REMEDIATE DIVISION_FOUND_IN_CALCULATION_ON_NON_PERCENT_DESCRIPTOR',
                'REPORT_UPDATE_MANAGER'
            );
        }
    }

    setCoreValue(descriptorId: string, newValue: any) {
        const entity = this.dryRunEntries.find((e) => e.descriptorId() === descriptorId);
        if (!entity) {
            Assert.fail(
                `reportDiffUtil > setReportedAsLabel > Entity was not found with the following data: reportId:${this.report.id} targetDescriptorId:${descriptorId}`
            );
            return;
        }

        entity.setData(newValue, undefined);
    }

    setReportedAsLabel(descriptorId: string, newValue: any) {
        const entity = this.dryRunEntries.find((e) => e.descriptorId() === descriptorId);

        if (!entity) {
            Assert.fail(
                `reportDiffUtil > setReportedAsLabel > Entity was not found with the following data: reportId:${this.report.id} targetDescriptorId:${descriptorId}`
            );
            return;
        }

        const newMetadata = { ...entity.getMetadata(), [MetadataField.REPORTED_AS_LABEL]: newValue };
        entity.setMetadata(newMetadata);
    }

    getDiffs() {
        return this.reportChanges;
    }

    getUpdatedReport() {
        // TODO IMPORTANT: This is here to account for fields with enabled calculation
        // it will override the field to it's previous value if there is an enabled calculation
        for (const entity of this.dryRunEntries) {
            entity.refresh();
        }
        return ReportUtils.cellEntitiesToReport(this.dryRunEntries, this.report);
    }
}
